'use client'

import { useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getImageUrl } from "@/shared/utils/image";

const galleryImages = Array.from({ length: 12 }, (_, index) => {
  const imageNumber = index + 1;

  return {
    src: `/convocation/convocation-image-${imageNumber}.jpg`,
    alt: `Convocation gallery image ${imageNumber}`,
  };
});

export const ConvocationGallerySection = () => {
  const [selectedImage, setSelectedImage] = useState<(typeof galleryImages)[number] | null>(null);

  return (
    <>
      <section id="gallery" className="px-6 py-16 lg:px-12 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {galleryImages.map((image, index) => (
              <motion.button
                key={image.src}
                type="button"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.12 }}
                transition={{ duration: 0.55, delay: index * 0.04 }}
                className="group relative aspect-[4/5] overflow-hidden rounded-[1.6rem] bg-stone-900 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-200/80"
                onClick={() => setSelectedImage(image)}
                aria-label={`Preview ${image.alt}`}
              >
                <Image
                  src={getImageUrl(image.src)}
                  alt={image.alt}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  sizes="(max-width: 768px) 100vw, 25vw"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.04)_0%,rgba(0,0,0,0.18)_45%,rgba(0,0,0,0.6)_100%)] opacity-70 transition-opacity duration-500 group-hover:opacity-100" />
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      <Dialog
        open={Boolean(selectedImage)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedImage(null);
          }
        }}
      >
        {selectedImage ? (
          <DialogContent
            showCloseButton={false}
            className="max-w-5xl overflow-hidden rounded-[2rem] border-stone-800 bg-stone-950 p-0 text-stone-50 shadow-[0_24px_100px_rgba(0,0,0,0.45)]"
          >
            <DialogTitle className="sr-only">{selectedImage.alt}</DialogTitle>
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute right-4 top-4 z-10 rounded-full bg-stone-900/90 px-3 py-2 text-xs uppercase tracking-[0.2em] text-stone-100 transition hover:bg-stone-800"
            >
              Close
            </button>
            <div className="relative aspect-4/3 w-full bg-stone-900">
              <Image
                src={getImageUrl(selectedImage.src)}
                alt={selectedImage.alt}
                fill
                className="object-contain"
                sizes="100vw"
                priority
              />
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
};
